import {
  BaseOption as SharedBaseOption,
  KeysToCamelCase,
  OptionOptional as SharedOptionOptional,
} from '@statsbomb/parachute-types';
import { ReactNode } from 'react';
import { Entity } from './entity';
import { LinkType, linkTypes } from './link';
import { NestedObject } from './object';
import { ItemsPerPage } from './table';

export interface OptionOptional extends SharedOptionOptional {
  tooltip?: { title: string; description?: string };
  isCore?: boolean;
  isDisabled?: boolean;
  isPhysical?: boolean;
  context?: string[];
}

export interface BaseOption<T> extends Omit<SharedBaseOption<T>, 'optional'> {
  optional?: OptionOptional;
  icon?: ReactNode;
}

export type NumberOption = BaseOption<number>;
export type StringOption = BaseOption<string>;

export interface Labelled<T> {
  label: string;
  value: T;
}

const isNumberOption = (maybeNumberOption: any): maybeNumberOption is NumberOption =>
  typeof maybeNumberOption === 'object' &&
  maybeNumberOption !== null &&
  'value' in maybeNumberOption &&
  typeof maybeNumberOption.value === 'number' &&
  'label' in maybeNumberOption;

export const isNumberOptionArray = (maybeNumberOptionArray: any): maybeNumberOptionArray is NumberOption[] =>
  Array.isArray(maybeNumberOptionArray) && maybeNumberOptionArray.every(isNumberOption);
export type GroupedOptions<T> = {
  header: string;
  options: BaseOption<T>[];
};

export type TranslatedColumnGroup = {
  header: string;
  options: GroupedOptions<string>[];
};

export type ItemsPerPageOption = BaseOption<ItemsPerPage>;

export type SBID = string | number;

export type Nullable<T> = T | null;

export interface LinkObject extends Record<string, string | number> {
  id: number;
  label: string;
}

export const isLinkObject = (value: NestedObject[string]): value is LinkObject => {
  const linkObject = value as LinkObject;

  return linkObject?.id !== undefined && linkObject?.label !== undefined;
};

export const isLinkType = (key: string): key is LinkType => linkTypes.includes(key as LinkType);

export type NonUndefined<T> = T extends undefined ? never : T;

// TODO (PPC-1784: https://statsbomb.atlassian.net/browse/PPC-1784)
export type Section = Entity | 'dataLocker' | 'game';

export type CamelCaseValues<T> = KeysToCamelCase<T>[keyof KeysToCamelCase<T>];
